/**
 * KernelForm — Core three-part Kernel editor
 *
 * Manages:
 *   1. Diagnosis      — what obstacle/challenge are we facing?
 *   2. Guiding Policy — overall approach to deal with the obstacle
 *   3. Coherent Actions — coordinated steps (max 5)
 *
 * Guardrails:
 *   - Revenue-language warning on Diagnosis & Guiding Policy
 *   - Coherent Actions capped at MAX_COHERENT_ACTIONS (5)
 *   - Tooltips explain each field's purpose
 */

import { useState, useCallback } from 'react';
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import { useStrategyKernelStore } from '../../../stores/prioritization/strategyKernelStore';
import type { StrategyKernel, CoherentAction } from '../../../types/prioritization/strategyKernel';
import { MAX_COHERENT_ACTIONS, detectsRevenueLang } from '../../../types/prioritization/strategyKernel';

// ============================================================================
// Helpers
// ============================================================================

function FieldHint({ text }: { text: string }) {
  return (
    <span className="group relative inline-flex">
      <InformationCircleIcon className="w-4 h-4 text-[var(--text-ter)] hover:text-[var(--text-ter)] cursor-help ml-1" />
      <span className="pointer-events-none absolute left-5 top-0 z-20 w-64 bg-[var(--bg)] border border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-ter)] opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {text}
      </span>
    </span>
  );
}

function RevenueLangWarning() {
  return (
    <div className="mt-1 flex items-start gap-1.5 text-xs text-[var(--mustard)] bg-[var(--mustard)]/20 border border-[var(--mustard)]/50 px-2.5 py-1.5">
      <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>
        This reads like a performance target or financial goal. Strategy is <em>how you overcome an obstacle</em>, not the result you hope to achieve. Consider reframing as a problem to solve.
      </span>
    </div>
  );
}

// ============================================================================
// Coherent Actions sub-editor
// ============================================================================

interface ActionsEditorProps {
  actions: CoherentAction[];
  onAdd: () => void;
  onUpdate: (id: string, field: keyof CoherentAction, value: string) => void;
  onRemove: (id: string) => void;
  isSaving: boolean;
}

function ActionsEditor({ actions, onAdd, onUpdate, onRemove, isSaving }: ActionsEditorProps) {
  const atLimit = actions.length >= MAX_COHERENT_ACTIONS;

  return (
    <div className="space-y-2">
      {actions.length === 0 && (
        <p className="text-sm text-[var(--text-ter)] italic px-1">
          No actions yet. Add up to {MAX_COHERENT_ACTIONS} focused, coordinated actions.
        </p>
      )}

      {actions.map((action, index) => (
        <div key={action.id} className="flex items-start gap-2 group border border-[var(--rule)] bg-[var(--bg-raised)] p-3">
          <div className="flex items-center gap-1 text-[var(--text-sec)] mt-1 shrink-0">
            <Bars3Icon className="w-4 h-4" />
            <span className="text-xs font-mono text-[var(--text-ter)] w-4 text-center">{index + 1}</span>
          </div>
          <div className="flex-1 space-y-1.5">
            <input
              type="text"
              value={action.title}
              onChange={e => onUpdate(action.id, 'title', e.target.value)}
              placeholder="Action title"
              className="w-full text-sm font-medium text-[var(--text-ter)] bg-transparent border-none outline-none focus:ring-0 placeholder-neutral-600"
              disabled={isSaving}
            />
            <textarea
              value={action.description ?? ''}
              onChange={e => onUpdate(action.id, 'description', e.target.value)}
              placeholder="Brief description (optional)"
              rows={2}
              className="w-full text-xs text-[var(--text-ter)] bg-[var(--bg-raised)]/50 rounded border border-[var(--rule)] px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              disabled={isSaving}
            />
          </div>
          <button
            onClick={() => onRemove(action.id)}
            disabled={isSaving}
            className="shrink-0 mt-0.5 text-[var(--text-sec)] hover:text-[var(--coral)] transition-colors opacity-0 group-hover:opacity-100"
            title="Remove action"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onAdd}
          disabled={atLimit || isSaving}
          className={`inline-flex items-center gap-1.5 text-sm font-medium  px-3 py-1.5 transition-colors ${
            atLimit
              ? 'text-[var(--text-sec)] cursor-not-allowed bg-[var(--bg-raised)] border border-[var(--rule)]'
              : 'text-[var(--text-ter)] hover:bg-[var(--slate)]/20 bg-[var(--bg-raised)] border border-[var(--slate)]'
          }`}
        >
          <PlusIcon className="w-4 h-4" />
          Add action
        </button>
        <span className={`text-xs ${atLimit ? 'text-[var(--mustard)] font-medium' : 'text-[var(--text-ter)]'}`}>
          {actions.length} / {MAX_COHERENT_ACTIONS}
          {atLimit && ' — limit reached'}
        </span>
      </div>

      {atLimit && (
        <div className="flex items-start gap-1.5 text-xs text-[var(--mustard)] bg-[var(--mustard)]/20 border border-[var(--mustard)]/50 px-2.5 py-1.5">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Good strategy is focused. A "dog's dinner" of many initiatives dilutes execution. Choose your {MAX_COHERENT_ACTIONS} most important, coordinated actions.
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main KernelForm
// ============================================================================

interface KernelFormProps {
  kernel: StrategyKernel;
}

export function KernelForm({ kernel }: KernelFormProps) {
  const { updateKernel, addCoherentAction, updateCoherentAction, removeCoherentAction } =
    useStrategyKernelStore();

  const [isSaving, setIsSaving] = useState(false);
  const [localDiagnosis, setLocalDiagnosis] = useState(kernel.diagnosis);
  const [localPolicy, setLocalPolicy] = useState(kernel.guidingPolicy);

  const showDiagnosisWarning = detectsRevenueLang(localDiagnosis);
  const showPolicyWarning = detectsRevenueLang(localPolicy);

  const saveField = useCallback(
    async (field: 'diagnosis' | 'guidingPolicy', value: string) => {
      setIsSaving(true);
      try {
        await updateKernel(kernel.id, { [field]: value });
      } finally {
        setIsSaving(false);
      }
    },
    [kernel.id, updateKernel]
  );

  const handleAddAction = useCallback(async () => {
    setIsSaving(true);
    try {
      await addCoherentAction(kernel.id, { title: '', description: '' });
    } finally {
      setIsSaving(false);
    }
  }, [kernel.id, addCoherentAction]);

  const handleUpdateAction = useCallback(
    async (id: string, field: keyof CoherentAction, value: string) => {
      await updateCoherentAction(kernel.id, id, { [field]: value });
    },
    [kernel.id, updateCoherentAction]
  );

  const handleRemoveAction = useCallback(
    async (id: string) => {
      setIsSaving(true);
      try {
        await removeCoherentAction(kernel.id, id);
      } finally {
        setIsSaving(false);
      }
    },
    [kernel.id, removeCoherentAction]
  );

  return (
    <div className="space-y-8">
      {/* ── Part 1: Diagnosis ─────────────────────────────────────── */}
      <section>
        <div className="flex items-center mb-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--slate)]/40 text-[var(--text-ter)] text-xs font-bold mr-2">1</span>
          <h3 className="text-sm font-semibold text-[var(--text-ter)]">Diagnosis</h3>
          <FieldHint text="Define the nature of the challenge or obstacle facing the organization. Simplify complexity by identifying the critical aspects of the situation — not the outcome you want, but the problem standing in the way." />
        </div>
        <textarea
          value={localDiagnosis}
          onChange={e => setLocalDiagnosis(e.target.value)}
          onBlur={() => saveField('diagnosis', localDiagnosis)}
          placeholder="What is the critical challenge or obstacle we face? What makes this moment difficult or dangerous? What aspects of our situation are most important to address?"
          rows={5}
          className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3.5 py-2.5 text-sm text-[var(--text-ter)] placeholder-neutral-600 outline-none focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)] resize-none"
          disabled={isSaving}
        />
        {showDiagnosisWarning && <RevenueLangWarning />}
        {!showDiagnosisWarning && localDiagnosis && (
          <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1">
            <span>✓</span> Reads like a problem diagnosis.
          </p>
        )}
      </section>

      {/* ── Part 2: Guiding Policy ────────────────────────────────── */}
      <section>
        <div className="flex items-center mb-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--slate)]/40 text-[var(--text-ter)] text-xs font-bold mr-2">2</span>
          <h3 className="text-sm font-semibold text-[var(--text-ter)]">Guiding Policy</h3>
          <FieldHint text="The overall approach or method chosen to cope with the obstacles in the diagnosis. This acts as a set of guardrails — it tells the organization what to do AND what not to do. It should channel energy, not just describe ambition." />
        </div>
        <textarea
          value={localPolicy}
          onChange={e => setLocalPolicy(e.target.value)}
          onBlur={() => saveField('guidingPolicy', localPolicy)}
          placeholder="What overall approach will we take to address the challenge? What constraints or rules will guide our decisions and resource allocation? What will we NOT do?"
          rows={4}
          className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3.5 py-2.5 text-sm text-[var(--text-ter)] placeholder-neutral-600 outline-none focus:ring-2 focus:ring-purple-500 focus:border-[var(--slate)] resize-none"
          disabled={isSaving}
        />
        {showPolicyWarning && <RevenueLangWarning />}
      </section>

      {/* ── Part 3: Coherent Actions ──────────────────────────────── */}
      <section>
        <div className="flex items-center mb-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-bold mr-2">3</span>
          <h3 className="text-sm font-semibold text-[var(--text-ter)]">Coherent Actions</h3>
          <FieldHint text="Feasible, coordinated steps that carry out the guiding policy. Each action should reinforce the others — they form a system, not a list. Limited to 5 to force hard choices and maintain focus." />
        </div>
        <ActionsEditor
          actions={kernel.coherentActions}
          onAdd={handleAddAction}
          onUpdate={handleUpdateAction}
          onRemove={handleRemoveAction}
          isSaving={isSaving}
        />
      </section>

      {/* ── Completeness indicator ───────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3">
        <p className="text-xs font-medium text-[var(--text-ter)] mb-2">Kernel completeness</p>
        <div className="flex gap-3">
          {[
            { label: 'Diagnosis', done: localDiagnosis.trim().length > 20, color: 'blue' },
            { label: 'Guiding Policy', done: localPolicy.trim().length > 20, color: 'purple' },
            { label: 'Coherent Actions', done: kernel.coherentActions.length > 0, color: 'green' },
          ].map(({ label, done, color }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${done ? `bg-${color}-500` : 'bg-[var(--surface)]'}`} />
              <span className={`text-xs ${done ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
